'use client';

/**
 * Ensures an anon session EXISTS before the shell (19369) create-profile screen renders.
 *
 * The shell create-profile screen (CreateProfileContainer → CreateProfileScreenV2) presupposes
 * an already-minted anon account — it lets the visitor "claim" it (pick a handle/avatar), and
 * the surface's settings/user reads (/apigw/v1/settings, /v1/user) all need a session token.
 * On the PARTNER surface that anon session is minted by PartnerSdkRuntime in the partner layout,
 * so /register there already has one. On ROOT there is no such runtime for a guest (root browses
 * free, session-less, by design), so a root guest who taps Store/Menu and lands on /register would
 * otherwise hang on the loading splash (settings 401) and never reach create-profile.
 *
 * This gate closes that gap: reached WITHOUT a session on the shell game, it mints the anon
 * account (the same POST /api/create-anon-profile the partner runtime uses), waits for the
 * session cookie, then router.refresh() — the server register page re-runs, now finds the
 * session, fetches the user and renders CreateProfileContainer. Idempotent: a surface that
 * already has a session never mounts this (the server page branches on the cookie), and if a
 * session appears mid-flight we just refresh. Shows the same LoadingScreen as the create-profile
 * chunk so the hop is a single continuous splash.
 */

import {useEffect, useRef, useState} from 'react';
import {useRouter} from 'next/navigation';
import {v4 as uuidv4} from 'uuid';
import Cookies from 'js-cookie';
import LoadingScreen from '@/app/(app)/games/[gid]/_src/LoadingScreen';
import {getCsrfToken} from '@/utils/getCsrfToken';
import {getEnvConfig} from '@/utils/getEnvConfig';
import {CSRF_HEADER, INSTALL_ID_COOKIE_NAME, PARTNER_SESSION_MINT_FLAG} from '@/utils/constant';

export default function ShellAnonMintGate({gid}: {gid: string}) {
  const router = useRouter();
  const started = useRef(false);
  // Terminal failure state: the mint errored/timed out, or the session cookie never
  // appeared within the poll ceiling. Without this the user spins on LoadingScreen forever.
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    let cancelled = false;
    const currentUserCookie = getEnvConfig().CURRENT_USER_COOKIE_NAME;

    // The session may already exist (a router.refresh() race, or a returning visitor):
    // just re-render so the server page picks it up.
    if (Cookies.get(currentUserCookie)) {
      router.refresh();
      return;
    }

    // The mint fingerprint needs an install id (parseNetworkRelayData reads it); the partner
    // runtime sets this during hydration, but root has no runtime — set it here if absent.
    if (!Cookies.get(INSTALL_ID_COOKIE_NAME)) {
      Cookies.set(INSTALL_ID_COOKIE_NAME, uuidv4(), {expires: 3650, path: '/'});
    }

    const waitForCookie = () => {
      let tries = 0;
      const poll = () => {
        if (cancelled) return;
        if (Cookies.get(currentUserCookie)) {
          router.refresh();
        } else if (tries++ < 40) {
          setTimeout(poll, 150); // ~6s ceiling, mirrors PartnerSdkRuntime
        } else {
          // Session cookie never showed up — stop spinning and surface a retry.
          setFailed(true);
        }
      };
      poll();
    };

    (async () => {
      try {
        const res = await fetch('/api/create-anon-profile', {
          method: 'POST',
          // Cap a hanging mint so it can't block the poll ceiling indefinitely.
          signal: AbortSignal.timeout(10000),
          headers: {
            'Content-Type': 'application/json',
            'x-skillz-game-id': gid,
            [CSRF_HEADER]: getCsrfToken(),
          },
          body: JSON.stringify({gid}),
        });
        if (!res.ok) throw new Error(`mint failed: ${res.status}`);
        // Share the partner tab-mint flag so PartnerSdkRuntime (if this hop ends on a
        // partner surface) sees the session as already minted and doesn't mint twice.
        try {
          sessionStorage.setItem(PARTNER_SESSION_MINT_FLAG, '1');
        } catch {
          /* sessionStorage unavailable — harmless, the cookie is the source of truth */
        }
        waitForCookie();
      } catch (e) {
        console.debug('[shell_register] anon session mint failed', String(e));
        // Mint failed outright — no cookie is coming, so go straight to the terminal state.
        if (!cancelled) setFailed(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [gid, router]);

  if (failed) {
    // Recoverable terminal state: a full reload re-runs the server register page, which
    // re-checks the session cookie and either lands on create-profile or re-mounts this gate.
    return (
      <div
        role='alert'
        className='flex min-h-screen flex-col items-center justify-center gap-4 px-6 text-center'
      >
        <p className='text-base font-medium'>We couldn’t start your session.</p>
        <button
          type='button'
          onClick={() => window.location.reload()}
          className='rounded-full bg-black px-6 py-2 text-sm font-semibold text-white'
        >
          Try again
        </button>
      </div>
    );
  }

  return <LoadingScreen />;
}
